import React from 'react'
import PropTypes from 'prop-types'
import { sanitizeUrl } from '../../../../global/formatters/sanitize-url.js'
import {
  getUrlParameter,
  adjustCWPMappedUrl,
} from '../../../../global/data/url.js'
import FundBasicsItem from '../Shared/FundBasicsItem.jsx'
import {
  ETF_FUND,
  CLOSED_END_FUND,
  FUTURE_SCHOLAR_529,
  VARIABLE_FUND,
  MODEL_PORTFOLIOS,
} from '../../Helpers/FundTypes.js'
import { formatDateStandard } from '../../../../global/formatters/string-formatter.js'
import Tooltip from '../../../Tooltip/Tooltip.jsx'
import {
  getETFTooltipContent,
  getMutualFundTooltipContent,
  getModelPortfolioTooltipContent,
  getTooltipContentByKey,
} from '../../../Tooltip/tooltipUtil.js'

function OverviewFundAtAGlance({
  fundSummary,
  portfolioHighlightDetail,
  fundGroup,
  sevenDayYields,
}) {
  const isEtf = fundGroup === ETF_FUND
  const {
    dailyPrice,
    yieldToWorstDetail,
    yearToDatePerformance,
    liquidAssetsDetail,
    distributionsDetail,
    fundBasicDetail,
    semiTransparentChars,
    modelBasicDetail,
  } = fundSummary
  const cusip = getUrlParameter('cusip')
  const mappedUrl = sanitizeUrl(adjustCWPMappedUrl('historical-data', cusip))
  const isModelPortfolio = fundGroup === MODEL_PORTFOLIOS

  let basicDetailDate = fundBasicDetail?.netAssetsAsOfDate
    ? fundBasicDetail.netAssetsAsOfDate
    : modelBasicDetail?.asOfDate
  if (
    fundGroup === FUTURE_SCHOLAR_529 &&
    portfolioHighlightDetail &&
    portfolioHighlightDetail.portfolioHighlightAsOfDate
  ) {
    basicDetailDate = portfolioHighlightDetail.portfolioHighlightAsOfDate
  }

  const etfTooltipData = getETFTooltipContent()
  const mutualFundTooltipData = getMutualFundTooltipContent()
  const modelPortfoliosTooltipData = getModelPortfolioTooltipContent()

  return (
    <div id="fund-at-a-glance">
      <div
        id="ampf-trace-loaded-section-fund-story-fund-at-a-glance"
        className="u-paddingBottom10gu"
      >
        <div className="Grid Grid--alignMiddle">
          <div className="u-paddingRight2gu u-sizeFill">
            <h3 className="u-marginBottom3gu">
              {isModelPortfolio ? 'Models At-a-Glance' : 'Fund At-a-Glance'}
            </h3>
          </div>
          {fundGroup !== CLOSED_END_FUND &&
            fundGroup !== VARIABLE_FUND &&
            fundGroup !== FUTURE_SCHOLAR_529 &&
            !isModelPortfolio && (
              <div className="u-sizeFit">
                <a
                  className="js-subnav-link Link u-textSecondary u-textSolid"
                  href="#definitions"
                >
                  View definitions
                </a>
              </div>
            )}
        </div>
        <div className="Grid-striper Reverse-striper u-marginHoriz2gu">
          {dailyPrice?.offeringPriceDate &&
          fundGroup !== CLOSED_END_FUND &&
          !isModelPortfolio ? (
            <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
              <div className="u-flexAlignSelfStart u-size3of12 u-sm-paddingBottom2gu u-sm-sizeFull">
                <h4 className="u-sm-inlineBlock">Daily Price</h4>
              </div>
              <div className="Grid u-size9of12 u-sm-sizeFull">
                <FundBasicsItem
                  label="PUV"
                  fundValue={dailyPrice.portfolioUnitValue}
                  preValueLabel="$"
                />
                <FundBasicsItem
                  label="Offering price"
                  fundValue={dailyPrice.offeringPrice}
                  preValueLabel="$"
                  subLabel="as of "
                  subLabelDate={dailyPrice.offeringPriceDate}
                />
                <FundBasicsItem
                  label={dailyPrice.navType}
                  fundValue={dailyPrice.netAssetValue}
                  preValueLabel="$"
                  subLabel="as of "
                  subLabelDate={dailyPrice.priceAsOfDate}
                />
                <FundBasicsItem
                  label="Change ($)"
                  fundValue={dailyPrice.dailyPriceChangeValue}
                  preValueLabel="$"
                  plusMinusLabel={true}
                  upDownArrowLabel={true}
                  subLabel="as of "
                  subLabelDate={dailyPrice.priceAsOfDate}
                />
                <FundBasicsItem
                  label="Change (%)"
                  fundValue={dailyPrice.dailyPriceChangePercent}
                  postValueLabel="%"
                  plusMinusLabel={true}
                  upDownArrowLabel={true}
                  subLabel="as of "
                  subLabelDate={dailyPrice.priceAsOfDate}
                />
              </div>
            </div>
          ) : (
            !isModelPortfolio && (
              <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
                <div
                  className={`u-flexAlignSelfStart ${
                    isEtf ? 'u-size2of12' : 'u-size3of12'
                  } u-sm-paddingBottom2gu u-sm-sizeFull`}
                >
                  <h4 className="u-sm-inlineBlock">Daily Price</h4>{' '}
                  <span className="u-textColorLight u-textSecondary">
                    as of {formatDateStandard(dailyPrice.priceAsOfDate)}
                  </span>
                </div>
                <div
                  className={`Grid ${
                    isEtf ? 'u-size10of12' : 'u-size9of12'
                  } u-sm-sizeFull`}
                >
                  <FundBasicsItem
                    label="PUV"
                    fundValue={dailyPrice.portfolioUnitValue}
                    preValueLabel="$"
                  />
                  <FundBasicsItem
                    label="Offering price"
                    fundValue={dailyPrice.offeringPrice}
                    preValueLabel="$"
                  />
                  <FundBasicsItem
                    label={dailyPrice.navType}
                    fundValue={dailyPrice.netAssetValue}
                    preValueLabel="$"
                  />

                  {isEtf ? (
                    <FundBasicsItem
                      label="Market Price"
                      fundValue={dailyPrice.marketPrice}
                      preValueLabel="$"
                      isTooltip={true}
                      tooltipHead={
                        getTooltipContentByKey(etfTooltipData, 'Market Price')
                          ?.heading || 'Market Price'
                      }
                      tooltipId="market-price"
                      tooltipDirection="Tooltip-right"
                      tooltipContent={
                        getTooltipContentByKey(etfTooltipData, 'Market Price')
                          ?.content || ''
                      }
                    />
                  ) : (
                    <FundBasicsItem
                      label="Market Price"
                      fundValue={dailyPrice.marketPrice}
                      preValueLabel="$"
                    />
                  )}
                  <FundBasicsItem
                    label="Premium / Discount ($)"
                    fundValue={dailyPrice.premiumDiscountAmount}
                    preValueLabel="$"
                    plusMinusLabel={true}
                    upDownArrowLabel={true}
                  />
                  <FundBasicsItem
                    label="Premium / Discount (%)"
                    fundValue={dailyPrice.premiumDiscountPercent}
                    postValueLabel="%"
                    plusMinusLabel={true}
                    upDownArrowLabel={true}
                  />
                  {isEtf ? (
                    <FundBasicsItem
                      label="Median Bid-Ask Spread"
                      fundValue={dailyPrice.thirtyDayMedBidAskSprdAmt}
                      postValueLabel="%"
                      isTooltip={true}
                      tooltipDirection="Tooltip-left"
                      tooltipHead={
                        getTooltipContentByKey(
                          etfTooltipData,
                          'Median Bid-Ask Spread'
                        )?.heading || 'Median Bid-Ask Spread'
                      }
                      tooltipId="median-bid"
                      tooltipContent={
                        getTooltipContentByKey(
                          etfTooltipData,
                          'Median Bid-Ask Spread'
                        )?.content || ''
                      }
                    />
                  ) : (
                    <FundBasicsItem
                      label="Median Bid-Ask Spread"
                      fundValue={dailyPrice.thirtyDayMedBidAskSprdAmt}
                      postValueLabel="%"
                    />
                  )}
                  <FundBasicsItem
                    label="Change ($)"
                    fundValue={dailyPrice.dailyPriceChangeValue}
                    preValueLabel="$"
                    plusMinusLabel={true}
                    upDownArrowLabel={true}
                  />
                  <FundBasicsItem
                    label="Change (%)"
                    fundValue={dailyPrice.dailyPriceChangePercent}
                    postValueLabel="%"
                    plusMinusLabel={true}
                    upDownArrowLabel={true}
                  />
                </div>
              </div>
            )
          )}

          {yearToDatePerformance && (
            <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
              <div className="u-flexAlignSelfStart u-size3of12 u-sm-paddingBottom2gu u-sm-sizeFull">
                <h4 className="u-sm-inlineBlock"> Year-to-Date Performance</h4>{' '}
                <span className="u-textColorLight u-textSecondary">
                  as of{' '}
                  {formatDateStandard(yearToDatePerformance.ytdPerfAsOfDate)}
                </span>
              </div>
              <div className="Grid u-size9of12 u-sm-sizeFull">
                <FundBasicsItem
                  label={yearToDatePerformance.portfolioUnitValueLabel}
                  fundValue={yearToDatePerformance.ytdPerfNAVPercent}
                  postValueLabel="%"
                />
                <FundBasicsItem
                  label={yearToDatePerformance.ytdPerfPOPPercentLabel}
                  fundValue={yearToDatePerformance.ytdPerfPOPPercent}
                  postValueLabel="%"
                />
              </div>
            </div>
          )}

          {liquidAssetsDetail &&
            liquidAssetsDetail.dailyLiquidAssetsPercent && (
              <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
                <div className="u-flexAlignSelfStart u-size3of12 u-sm-paddingBottom2gu u-sm-sizeFull">
                  <h4 className="u-sm-inlineBlock">Liquidity</h4>
                </div>
                <div className="Grid u-size9of12 u-sm-sizeFull">
                  <FundBasicsItem
                    label="Daily liquid assets"
                    fundValue={liquidAssetsDetail.dailyLiquidAssetsPercent}
                    postValueLabel="%"
                    subLabel="as of "
                    subLabelDate={liquidAssetsDetail.liquidAssetAsOfDate}
                  />
                  <FundBasicsItem
                    label="Weekly liquid assets"
                    fundValue={liquidAssetsDetail.weeklyLiquidAssetsPercent}
                    postValueLabel="%"
                    subLabel="as of "
                    subLabelDate={liquidAssetsDetail.liquidAssetAsOfDate}
                  />
                  <FundBasicsItem
                    label="Net shareholder flows"
                    fundValue={liquidAssetsDetail.netShareholdFlowsAmt}
                    subLabel="as of "
                    subLabelDate={liquidAssetsDetail.netShareholdFlowsAsOfDate}
                    preValueLabel="$"
                  />
                </div>
              </div>
            )}

          {distributionsDetail &&
            (distributionsDetail.annualDistRateEffDate ||
              distributionsDetail.annualDistRateNAVPercent ||
              distributionsDetail.thirtyDaySecYieldPercent ||
              distributionsDetail.thirtyDayUnSubSecYieldPercent ||
              distributionsDetail.distrSchedule) && (
              <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
                <div
                  className={`u-flexAlignSelfStart ${
                    isEtf ? 'u-size2of12' : 'u-size3of12'
                  } u-sm-paddingBottom2gu u-sm-sizeFull`}
                >
                  <h4 className="u-sm-inlineBlock">Distributions</h4>
                  {distributionsDetail.annualDistRateEffDate && (
                    <React.Fragment>
                      <span className="u-textColorLight u-textSecondary u-sm-marginLeft1gu">
                        as of{' '}
                        {formatDateStandard(
                          distributionsDetail.annualDistRateEffDate
                        )}
                      </span>
                    </React.Fragment>
                  )}
                </div>
                <div
                  className={`Grid ${
                    isEtf ? 'u-size10of12' : 'u-size9of12'
                  } u-sm-sizeFull`}
                >
                  <FundBasicsItem
                    label="Annual distribution rate at NAV"
                    fundValue={distributionsDetail.annualDistRateNAVPercent}
                    postValueLabel="%"
                    isTooltip={true}
                    tooltipDirection="Tooltip-left"
                    tooltipHead={
                      getTooltipContentByKey(
                        mutualFundTooltipData,
                        'Annual distribution rate at NAV'
                      )?.heading || 'Annual distribution rate at NAV'
                    }
                    tooltipId="tooltip-annual-dis-rate"
                    tooltipContent={
                      getTooltipContentByKey(
                        mutualFundTooltipData,
                        'Annual distribution rate at NAV'
                      )?.content || ''
                    }
                  />

                  <FundBasicsItem
                    label="30-day SEC yield"
                    fundValue={distributionsDetail.thirtyDaySecYieldPercent}
                    postValueLabel="%"
                    isTooltip={true}
                    tooltipDirection="Tooltip-left"
                    tooltipHead={
                      getTooltipContentByKey(
                        mutualFundTooltipData,
                        '30-day SEC yield'
                      )?.heading || '30-day SEC yield'
                    }
                    tooltipId="tooltip-30day-sec-yield"
                    tooltipContent={
                      getTooltipContentByKey(
                        mutualFundTooltipData,
                        '30-day SEC yield'
                      )?.content || ''
                    }
                  />

                  <FundBasicsItem
                    label="Unsubsidized 30-day SEC yield"
                    fundValue={
                      distributionsDetail.thirtyDayUnSubSecYieldPercent
                    }
                    postValueLabel="%"
                    isTooltip={true}
                    tooltipDirection="Tooltip-left"
                    tooltipHead={
                      getTooltipContentByKey(
                        mutualFundTooltipData,
                        'Unsubsidized 30-day SEC yield'
                      )?.heading || 'Unsubsidized 30-day SEC yield'
                    }
                    tooltipId="tooltip-unsubsidized-30day-sec-yield"
                    tooltipContent={
                      getTooltipContentByKey(
                        mutualFundTooltipData,
                        'Unsubsidized 30-day SEC yield'
                      )?.content || ''
                    }
                  />
                  {yieldToWorstDetail && (
                    <FundBasicsItem
                      label="Yield to Worst"
                      fundValue={yieldToWorstDetail.fundValue}
                      postValueLabel="%"
                      isTooltip={true}
                      tooltipDirection="Tooltip-left"
                      tooltipHead={
                        getTooltipContentByKey(
                          mutualFundTooltipData,
                          'Yield to Worst'
                        )?.heading || ''
                      }
                      tooltipId="tooltip-unsubsidized-30day-sec-yield"
                      tooltipContent={
                        getTooltipContentByKey(
                          mutualFundTooltipData,
                          'Yield to Worst'
                        )?.content || ''
                      }
                    />
                  )}
                  <FundBasicsItem
                    label="Distribution schedule"
                    fundValue={distributionsDetail.distrSchedule}
                  />
                  {sevenDayYields && (
                    <>
                      <FundBasicsItem
                        label="7-day current yield"
                        fundValue={sevenDayYields.currentValue}
                        subLabelDate={sevenDayYields.currentDate}
                      />
                      <FundBasicsItem
                        label="Unsubsidized 7-day current yield"
                        fundValue={sevenDayYields.unSubValue}
                        subLabelDate={sevenDayYields.unSubDate}
                      />
                    </>
                  )}
                </div>
              </div>
            )}

          {fundBasicDetail &&
            (basicDetailDate ||
              fundBasicDetail.fmtTotalNetAssets ||
              fundBasicDetail.minimumInvestment ||
              fundBasicDetail.bloombergIndexTicker ||
              fundBasicDetail.ticker ||
              fundBasicDetail.cusip ||
              fundBasicDetail.inceptionDate ||
              fundBasicDetail.exchangeNm) && (
              <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
                <div
                  className={`u-flexAlignSelfStart ${
                    isEtf ? 'u-size2of12' : 'u-size3of12'
                  } u-sm-paddingBottom2gu u-sm-sizeFull`}
                >
                  <h4 className="u-sm-inlineBlock">Fund Basics</h4>
                  <span className="u-textColorLight u-textSecondary u-sm-marginLeft1gu">
                    {!isEtf && (
                      <React.Fragment>
                        as of {formatDateStandard(basicDetailDate)}
                      </React.Fragment>
                    )}
                  </span>
                </div>
                <div
                  className={`Grid ${
                    isEtf ? 'u-size10of12' : 'u-size9of12'
                  } u-sm-sizeFull`}
                >
                  <FundBasicsItem
                    label="Total net assets"
                    fundValue={fundBasicDetail.fmtTotalNetAssets}
                    preValueLabel="$"
                  />
                  <FundBasicsItem
                    label="Minimum investment"
                    fundValue={fundBasicDetail.minimumInvestment}
                  />
                  <FundBasicsItem
                    label={
                      semiTransparentChars ? 'Ticker' : 'Bloomberg Index Ticker'
                    }
                    fundValue={
                      semiTransparentChars
                        ? fundBasicDetail.ticker
                        : fundBasicDetail.bloombergIndexTicker
                    }
                  />
                  <FundBasicsItem
                    label="CUSIP"
                    fundValue={fundBasicDetail.cusip}
                  />
                  <FundBasicsItem
                    label={
                      fundGroup === FUTURE_SCHOLAR_529
                        ? 'Inception date'
                        : 'Fund inception date'
                    }
                    fundValue={formatDateStandard(
                      fundBasicDetail.inceptionDate
                    )}
                  />
                  <FundBasicsItem
                    label="Exchange"
                    fundValue={fundBasicDetail.exchangeNm}
                  />
                  <FundBasicsItem
                    label="Initial investment"
                    fundValue={fundBasicDetail.initialInvestment}
                  />
                  <FundBasicsItem
                    label="Subsequent investment"
                    fundValue={fundBasicDetail.subsequentInvestment}
                  />
                  <FundBasicsItem
                    label="IPO date"
                    fundValue={formatDateStandard(fundBasicDetail.ipoDate)}
                  />
                  <FundBasicsItem
                    label="Commencement of operations"
                    fundValue={formatDateStandard(
                      fundBasicDetail.commencementOfOperationsDate
                    )}
                  />
                </div>
                {(semiTransparentChars ||
                  fundBasicDetail.trackingIndex ||
                  fundBasicDetail.benchmarkIndex) && (
                  <div className="js-basic-item u-size10of12 u-before2of12 u-marginTop4gu u-sm-beforeNone u-sm-sizeFull u-marginBottom3gu">
                    <p className="u-textColorLight u-textSecondary u-textSolid">
                      {fundBasicDetail.trackingIndex
                        ? 'Tracking Index'
                        : 'Benchmark Index'}
                    </p>
                    <p className="u-textSizeMsrp3 u-textSecondary u-sm-textSizeMsrp1">
                      {fundBasicDetail.trackingIndex
                        ? fundBasicDetail.trackingIndex
                        : fundBasicDetail.benchmarkIndex}
                    </p>
                  </div>
                )}
              </div>
            )}
          {modelBasicDetail &&
            (basicDetailDate ||
              modelBasicDetail.avgUndExp ||
              modelBasicDetail.weightAnnYld) && (
              <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
                <div
                  className={`u-flexAlignSelfStart ${
                    isEtf ? 'u-size2of12' : 'u-size3of12'
                  } u-sm-paddingBottom2gu u-sm-sizeFull`}
                >
                  <h4 className="u-sm-inlineBlock">Portfolio Basics</h4>
                  <span className="u-textColorLight u-textSecondary u-sm-marginLeft1gu">
                    <React.Fragment>
                      as of {formatDateStandard(basicDetailDate)}
                    </React.Fragment>
                  </span>
                </div>
                <div
                  className={`Grid ${
                    isEtf ? 'u-size10of12' : 'u-size9of12'
                  } u-sm-sizeFull`}
                >
                  <FundBasicsItem
                    label="Average Underlying Expenses"
                    fundValue={modelBasicDetail.avgUndExp}
                    postValueLabel="%"
                    isTooltip={true}
                    tooltipDirection="Tooltip-left"
                    tooltipHead={
                      getTooltipContentByKey(
                        modelPortfoliosTooltipData,
                        'Average Underlying Expenses'
                      )?.heading || 'Average Underlying Expenses'
                    }
                    tooltipId="tooltip-average-underlying-expenses"
                    tooltipContent={
                      getTooltipContentByKey(
                        modelPortfoliosTooltipData,
                        'Average Underlying Expenses'
                      )?.content || ''
                    }
                  />
                  <FundBasicsItem
                    label="Trailing 12-Month Yield"
                    fundValue={modelBasicDetail.weightAnnYld}
                    postValueLabel="%"
                    isTooltip={true}
                    tooltipDirection="Tooltip-left"
                    tooltipHead={
                      getTooltipContentByKey(
                        modelPortfoliosTooltipData,
                        'Trailing 12-Month Yield'
                      )?.heading || 'Trailing 12-Month Yield'
                    }
                    tooltipId="tooltip-trailing-12m-yield"
                    tooltipContent={
                      getTooltipContentByKey(
                        modelPortfoliosTooltipData,
                        'Trailing 12-Month Yield'
                      )?.content || ''
                    }
                  />
                </div>
              </div>
            )}

          {isEtf && semiTransparentChars && (
            <div className="Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu u-paddingTop3gu">
              <div
                className={`u-flexAlignSelfStart ${
                  isEtf ? 'u-size2of12' : 'u-size3of12'
                } u-sm-paddingBottom2gu u-sm-sizeFull`}
              >
                <h4 className="u-sm-inlineBlock">
                  Semi-Transparent ETF Characteristics
                </h4>
              </div>
              <div className={`Grid u-size10of12 u-sm-sizeFull`}>
                <div className="js-basic-item u-lg-size3of12 u-md-size4of12 u-sm-size6of12 u-marginBottom3gu fund-item u-sm-posRelative Tooltip-posleft">
                  <Tooltip
                    heading={
                      getTooltipContentByKey(etfTooltipData, 'Tracking Basket')
                        ?.heading || 'Tracking Basket'
                    }
                    content={
                      getTooltipContentByKey(etfTooltipData, 'Tracking Basket')
                        ?.content || ''
                    }
                    direction="Tooltip-left"
                    id="tracking-basket"
                  >
                    <span className="u-textColorLight u-textSecondary Tooltip-label">
                      Tracking Basket *
                    </span>
                    <span className="Icon--material Tooltip-icon u-marginLeft1gu">
                      info
                    </span>
                  </Tooltip>
                  <a
                    className="Link u-block u-textSecondary u-sm-textSizeMsrm1 u-paddingTop2gu"
                    href={`/cmg.svc/exportETFTrackingBasket?fundGroupName=ETF&fileType=csv&cusip=${fundBasicDetail.cusip}`}
                  >
                    Download File (CSV)
                  </a>
                </div>
                <FundBasicsItem
                  label="Tracking Basket Weight Overlap**"
                  fundValue={semiTransparentChars.trackingBasketWeightOverlap}
                  postValueLabel="%"
                  isTooltip={true}
                  tooltipHead={
                    getTooltipContentByKey(
                      etfTooltipData,
                      'Tracking Basket Weight Overlap'
                    )?.heading || 'Tracking Basket Weight Overlap'
                  }
                  tooltipContent={
                    getTooltipContentByKey(
                      etfTooltipData,
                      'Tracking Basket Weight Overlap'
                    )?.content || ''
                  }
                  tooltipDirection="Tooltip-right"
                  tooltipId="weight-overlap"
                />
              </div>
            </div>
          )}

          {portfolioHighlightDetail && (
            <div
              className={`Grid Grid--alignBottom Grid--withGutter js-fund-basic-category u-paddingHoriz1gu ${
                portfolioHighlightDetail.riskTrackCusips?.length > 0 &&
                'u-paddingTop3gu'
              }`}
            >
              <div
                className={`u-flexAlignSelfStart ${
                  isEtf ? 'u-size2of12' : 'u-size3of12'
                } u-sm-paddingBottom2gu u-sm-sizeFull`}
              >
                <h4 className="u-sm-inlineBlock"></h4>
                <span className="u-textColorLight u-textSecondary u-sm-marginLeft1gu"></span>
              </div>
              <div
                className={`Grid ${
                  isEtf ? 'u-size10of12' : 'u-size9of12'
                } u-sm-sizeFull`}
              >
                {portfolioHighlightDetail &&
                  portfolioHighlightDetail.riskTrackCusips &&
                  portfolioHighlightDetail.riskTrackCusips.map(
                    (trackingCusip, i) => {
                      return (
                        <FundBasicsItem
                          key={`trackingCusip${i}`}
                          label={trackingCusip.riskTrackName}
                          fundValue={trackingCusip.riskTrackCusip}
                        />
                      )
                    }
                  )}
              </div>
            </div>
          )}

          {fundBasicDetail?.historicalData === 'Yes' && (
            <a
              className="js-historical-data-link Link u-textSecondary u-marginTop4gu"
              href={encodeURI(`${mappedUrl}`)}
            >
              View historical data
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

OverviewFundAtAGlance.propTypes = {
  fundSummary: PropTypes.object,
  portfolioHighlightDetail: PropTypes.object,
  fundGroup: PropTypes.string,
  sevenDayYields: PropTypes.object,
}

export default OverviewFundAtAGlance
